/**
 * 銀行・支店検索（zengin-code 同梱データ）。
 * 全国の全金融機関（銀行・信金・信組・労金・JA・ゆうちょ等）＋全支店を
 * オフラインで検索する。外部API（ページング・レート制限・障害）に依存しない。
 *
 * データ（約5MB）は初回検索時に動的importで遅延ロードし、別チャンクとして
 * 銀行入力画面でのみ読み込まれる（メインバンドルは肥大化しない）。
 */

export const MIN_BANK_QUERY = 1;
export const MIN_BRANCH_QUERY = 1;

// 1回の検索で返す最大件数（候補リスト表示用）
const MAX_RESULTS = 50;

// zengin-code データの遅延ロード（初回のみ。以降はキャッシュ）
let _zenginPromise = null;
const loadZengin = () => {
  if (!_zenginPromise) {
    _zenginPromise = import('zengin-code').then((m) => m.default || m);
  }
  return _zenginPromise;
};

// 検索用の正規化（前後空白除去・小文字化・空白除去）
const norm = (s) => String(s || '').trim().toLowerCase().replace(/[\s　]+/g, '');

// 表示側が期待する形（normalize.name / normalize.kana を含む）に整形
const toOption = (o) => ({
  code: o.code,
  name: o.name,
  kana: o.kana,
  hira: o.hira,
  roma: o.roma,
  normalize: { name: o.name, kana: o.kana, hira: o.hira, roma: o.roma },
});

// 名称（漢字/カナ/ひらがな/ローマ字）・コードのいずれかに一致するか
const matchesQuery = (o, q) =>
  norm(o.name).includes(q) ||
  norm(o.kana).includes(q) ||
  norm(o.hira).includes(q) ||
  norm(o.roma).includes(q) ||
  String(o.code).includes(q);

/**
 * 銀行名検索（全金融機関から部分一致）。
 */
export const searchBanks = async (q) => {
  const t = norm(q);
  if (t.length < MIN_BANK_QUERY) return [];
  try {
    const data = await loadZengin();
    const out = [];
    for (const code of Object.keys(data)) {
      const b = data[code];
      if (matchesQuery(b, t)) {
        out.push(toOption(b));
        if (out.length >= MAX_RESULTS) break;
      }
    }
    return out;
  } catch (e) {
    console.warn('銀行検索エラー', e);
    return [];
  }
};

/**
 * 支店名検索（指定銀行の全支店から部分一致）。
 */
export const searchBranches = async (bankCode, q) => {
  const t = norm(q);
  if (!bankCode || t.length < MIN_BRANCH_QUERY) return [];
  try {
    const data = await loadZengin();
    const bank = data[String(bankCode)];
    if (!bank || !bank.branches) return [];
    const out = [];
    for (const bc of Object.keys(bank.branches)) {
      const br = bank.branches[bc];
      if (matchesQuery(br, t)) {
        out.push(toOption(br));
        if (out.length >= MAX_RESULTS) break;
      }
    }
    return out;
  } catch (e) {
    console.warn('支店検索エラー', e);
    return [];
  }
};
